import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, Loader2, Pencil, ExternalLink, BadgeCheck, ShieldCheck } from "lucide-react";

// Lightweight list of organizations the professional owns/administers, each linking to the
// owner/admin Edit surface. No create flow (P1.1).
export default function MyOrganizations() {
  const [orgs, setOrgs] = useState(undefined);

  useEffect(() => {
    let alive = true;
    api.get("/me/organizations")
      .then(({ data }) => alive && setOrgs(data || []))
      .catch((e) => { if (alive) { setOrgs([]); toast.error(apiError(e)); } });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-organizations">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-2xl font-bold text-[#0F2C59] mb-1">My organizations</h1>
        <p className="text-sm text-slate-500 mb-6">Organizations you own or administer. Edit profiles, services and public details.</p>

        {orgs === undefined ? (
          <div className="flex items-center justify-center py-24 text-slate-400" data-testid="my-orgs-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : orgs.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="my-orgs-empty">
            <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">You don't manage any organizations yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orgs.map((o) => (
              <div key={o.id} data-testid={`my-org-${o.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                <span className="h-12 w-12 rounded-xl bg-[#0F2C59]/8 flex items-center justify-center overflow-hidden shrink-0">
                  {o.logo ? <img src={o.logo} alt="" className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-[#0F2C59]" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-semibold text-[#0F2C59] truncate">{o.name}</span>
                    {o.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" />}
                    {o.is_partner && <ShieldCheck className="h-4 w-4 text-[#C79A3B] shrink-0" />}
                  </div>
                  <div className="mt-0.5 text-[13px] text-slate-500 truncate">{o.type_label} · {o.status}{o.my_role ? ` · ${o.my_role === "OWNER" ? "Owner" : "Admin"}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {o.slug && o.status === "PUBLISHED" && (
                    <a href={`/org/${o.slug}`} target="_blank" rel="noreferrer" data-testid={`my-org-view-${o.id}`} className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-[#0F2C59] hover:bg-slate-50"><ExternalLink className="h-4 w-4" /></a>
                  )}
                  <Link to={`/organizations/${o.id}/edit`} data-testid={`my-org-edit-${o.id}`}>
                    <Button className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
